import React from "react";
import {
  Button,
  Link as MuiLink,
  List,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import he from "he";
import { Link } from "react-router-dom";
import { RoutePath } from "../../enums/RoutePath";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useAppSelector } from "../../store/hooks";
import { CustomCircle, CustomListItem } from "./ContentPage";
import { selectApiKey, selectISIN } from "../../store/status/statusSlice";
import { useGetSeriesByISINAndApiKeyQuery, useGetStaticContentQuery } from "../../store/services/loanNote";
import { StaticContent } from "../../enums/StaticContent";

export default function PersonalContent() {
  const apiKey = useAppSelector(selectApiKey);

  const storedISIN = useAppSelector(selectISIN);

  const { data: staticContent, isFetching } = useGetStaticContentQuery();

  const { data: series } = useGetSeriesByISINAndApiKeyQuery(
    storedISIN && apiKey
      ? { ISIN: storedISIN, apiKey: apiKey }
      : skipToken
  );

  return (
    <>
      <Typography
        variant="h5"
        component="h2"
        color="text.secondary"
        letterSpacing="0.04em"
        mt={3}
        align="left"
      >
        COMPLETION AND SUBMISSION INSTRUCTIONS
      </Typography>
      <Typography
        variant="h6"
        component="h3"
        color="primary.main"
        align="left"
        mt={3}
      >
        Introduction
      </Typography>
      <Typography variant="body1" mt={2}>
        This form (the “Subscription Form”) is for the exclusive use of applicants (the “Applicant”)
        subscribing for the {series?.nickname} (the "Notes") to be issued by {series?.issuer_name} (the "issuer")
        on the terms set out in the information memorandum relating to the Notes (the “Information Memorandum”).
      </Typography>
      <Typography
        variant="h6"
        component="h3"
        color="primary.main"
        align="left"
        mt={3}
      >
        Important Notes
      </Typography>
      <List>
        <CustomListItem>
          <ListItemIcon>
            <CustomCircle />
          </ListItemIcon>
          <ListItemText>
            All Subscription Forms will be processed on behalf of the Issuer
            by {series?.nominee_name}.
          </ListItemText>
        </CustomListItem>
        <CustomListItem>
          <ListItemIcon>
            <CustomCircle />
          </ListItemIcon>
          <ListItemText>
            All questions in this Subscription Form must be answered, if a question is not applicable to you,
            please answer “n/a”.
            Once received, we will review the questionnaire, ask you for any
            clarifications or additional information (if required), within{" "}
            <Typography fontWeight="700" component="b">
              2 business days
            </Typography>
            , following which we will send you a unique link to yourself to
            undergo proof of identity (passport) check and proof of address
            (utility bill) check. Therefore, you are not required to send a copy
            of your passport and/or utility bill with this questionnaire as
            these will be checked electronically. Upon proof of identity and
            proof of address checks being complete, you will receive all
            documentation pertaining to our services, including this
            questionnaire, to be signed electronically.
          </ListItemText>
        </CustomListItem>
        <CustomListItem>
          <ListItemIcon>
            <CustomCircle />
          </ListItemIcon>
          <ListItemText>
            Documents provided must not be older than 3 months.
          </ListItemText>
        </CustomListItem>
        <CustomListItem>
          <ListItemIcon>
            <CustomCircle />
          </ListItemIcon>
          <ListItemText>
            Utility bills of mobile telephony or internet connection are not
            accepted.
          </ListItemText>
        </CustomListItem>
        <CustomListItem>
          <ListItemIcon>
            <CustomCircle />
          </ListItemIcon>
          <ListItemText>
            We reserve the right to request additional due diligence
            documentation (other than the documentation set out in this
            questionnaire) to satisfy our obligations under applicable
            anti-money laundering and funding of terrorism legislation and
            regulations.
          </ListItemText>
        </CustomListItem>
        <CustomListItem>
          <ListItemIcon>
            <CustomCircle />
          </ListItemIcon>
          <ListItemText>
            Please ensure you have all the required supporting documentation
            before submitting this questionnaire.
          </ListItemText>
        </CustomListItem>
        <CustomListItem>
          <ListItemIcon>
            <CustomCircle />
          </ListItemIcon>
          <ListItemText>
            The contents of this questionnaire shall be treated in strict
            confidence. All information contained herein must be correct as at
            time of completion and we must be informed immediately of any
            changes thereto. In the event that incorrect information is supplied
            to us or if we are not informed of any changes, we reserve the right
            not to provide/continue providing our services to you.
          </ListItemText>
        </CustomListItem>
      </List>

      <Typography
        variant="h6"
        component="h3"
        color="primary.main"
        align="left"
        mt={3}
      >
        Disclaimer
      </Typography>
      <div
        dangerouslySetInnerHTML={{
          __html: he.decode(staticContent?.[StaticContent.Disclaimer] ?? ""),
        }}
      />

      <Button
        size="large"
        variant="outlined"
        component={Link}
        to={RoutePath.FiduciaryApplicationForm}
        sx={{
          display: "block",
          width: "fit-content",
          mt: 4,
          mx: "auto",
          "& a": {
            textDecoration: "none",
            color: "primary.main",
          },
        }}
      >
        I agree to the above terms
      </Button>
    </>
  );
}
